<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <h3 class="kgr-headline kgr-headline--h3">
                {{ $t('Zeitraum der Rechnungsstellung') }}
            </h3>
        </div>
        <div class="kgr-inputGroup">
            <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Start')"
                        :error="error"
                        field-name="start"
                    >
                        <kgr-date-picker
                            v-model="start"
                        />
                    </kgr-form-row>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Ende')"
                            :error="error"
                            field-name="end"
                        >
                            <kgr-date-picker
                                v-model="end"
                            />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div class="kgr-row kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell">
                <kgr-checkbox
                    v-model="filters.invoiceStatusOpen"
                    :label="$t('Offene Rechnungen berücksichtigen')"
                />
            </div>
            <div class="kgr-grid__cell">
                <kgr-checkbox
                    v-model="filters.splitFiles"
                    :label="$t('Transaktionen auf mehrere XML-Dateien aufteilen')"
                />
            </div>
        </div>
        <div class="kgr-row kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell">
                <kgr-checkbox
                    v-model="filters.invoiceStatusPartial"
                    :label="$t('Teilbezahlte Rechnungen berücksichtigen')"
                />
            </div>
            <div class="kgr-grid__cell">
                <kgr-form-row
                    :label="$t('Max. Transaktionen pro Datei')"
                    :error="error"
                    :tooltip="$t('Bitte gib einen Wert zwischen 50 und 999 ein')"
                    field-name="maxTransactionsPerFile"
                >
                    <input
                        v-model.number="filters.maxTransactionsPerFile"
                        type="number"
                        class="kgr-input"
                        min="50"
                        max="999"
                        :disabled="!filters.splitFiles"
                    />
                </kgr-form-row>
            </div>
        </div>
        <div class="kgr-row kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell"></div>
            <div class="kgr-grid__cell">
                <kgr-form-row
                    :label="$t('Tag der Wertstellung')"
                    :error="error"
                    field-name="valuationDate"
                >
                    <kgr-date-picker
                        v-model="filters.valuationDate"
                    />
                </kgr-form-row>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div
            class="kgr-alert kgr-alert--info kgr-row"
        >
            <span class="kgr-text kgr-text--bold">
                <kgr-icon
                    icon-class="info-circle"
                    style="margin-bottom: -5px;margin-right:5px;color:black;"
                    disabled
                    small
                />{{ $t('Hinweis') }}</span><br />
            {{ $t('Rechnungen, die im Mahnstatus stehen oder als Rücklastschrift markiert wurden, werden beim SEPA-Export nicht mit berücksichtigt.') }}
        </div>
        <div
            class="kgr-alert kgr-alert--info kgr-row kgr-postfix kgr-postfix--gutters kgr-postfix--top"
        >
            <div
                class="kgr-postfix__fluid"
            >
                <div
                    v-if="exportDisabled"
                >
                    <div class="kgr-row">
                        {{ availability.messages[0] }}
                    </div>
                    <ul
                        v-for="(message, index) in availability.messages"
                        :key="index"
                        class="kgr-list kgr-list--bulleted"
                    >
                        <li
                            v-if="index > 0"
                            v-html="message"
                        >
                        </li>
                    </ul>
                </div>
            </div>
            <div class="kgr-postfix__fixed">
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="exportDisabled"
                    @click="createSepaExport()"
                >
                    {{ $t('SEPA-Lastschrift-Export erstellen') }}
                </button>
            </div>
        </div>
        <hr class="kgr-separator--light" />
        <div class="kgr-row">
            <h2 class="kgr-headline kgr-headline--h2">
                {{ $t('SEPA-Lastschrift-Exporte') }}
            </h2>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Export vom') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Tag der Wertstellung') }}
                    </th><th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loadingAvailability">
                    <td
                        colspan="3"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="sepaExports.length === 0">
                    <td
                        colspan="3"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="sepaExport in sepaExports"
                        :key="sepaExport.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ formatDateTime(sepaExport.createdAt) }} <br />
                            {{ sepaExport.numberOfInvoices }} Rechnung(en)
                            <template v-if="sepaExport.isZipFile">
                                <br />
                                <span> ({{ sepaExport.fileCount }} XML-Dateien)</span>
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            {{ formatDate(sepaExport.valuationDate) }} <br />
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                            <button
                                class="kgr-table__action"
                                @click="download(sepaExport)"
                            >
                                <kgr-icon icon-class="save" />
                            </button>
                            <button
                                v-if="isDeletable && withinLastWeek(sepaExport)"
                                class="kgr-table__action"
                                @click="deleteExport(sepaExport)"
                            >
                                <kgr-icon icon-class="delete" />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import { addDays, format, parse, subDays } from '@legacy/js/date/date-fns.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubRole from '../../../js/dto/ClubRole.js';
import ClubRoleRepository from '../../../js/repository/ClubRoleRepository.js';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Loading from '../../../components/common/Loading.vue';
import SepaDirectDebitExportAvailability from '../../../js/dto/SepaDirectDebitExportAvailability.js';
import SepaDirectDebitExportRepository from '../../../js/repository/SepaDirectDebitExportRepository.js';

export default {
    components: {
        'kgr-date-picker': DatePicker,
        'kgr-form-row': FormRow,
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-loader': Loading,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            loadingCreate: false,
            start: format(subDays(new Date(), 7), 'dd.MM.yyyy'),
            end: format(new Date(), 'dd.MM.yyyy'),
            sepaExports: [],
            loadingAvailability: false,
            filters: {
                invoiceStatusOpen: true,
                invoiceStatusPartial: false,
                splitFiles: false,
                maxTransactionsPerFile: 999,
                valuationDate: format(addDays(new Date(), 5), 'dd.MM.yyyy'),
            },
            availability: new SepaDirectDebitExportAvailability(),
            reloadExports: false,
            clubRoles: [],
        }
    },
    computed: {
        exportDisabled() {
            return this.loadingCreate
                || this.loadingAvailability
                || !this.availability.isAvailable
                || (!this.filters.invoiceStatusOpen && !this.filters.invoiceStatusPartial);
        },
        isDeletable() {
            return this.clubRoles.length > 0 && (this.clubRoles.includes(ClubRole.ADMIN));
        },
        minDate() {
            return new Date().toISOString().split('T')[0];
        },
        invoiceStatus() {
            const invoiceStatusMap = {
                invoiceStatusOpen: 'open',
                invoiceStatusPartial: 'unequal',
            };
            return Object.keys(invoiceStatusMap)
                .filter(key => this.filters[key])
                .map(key => invoiceStatusMap[key]);
        },
    },
    watch: {
        'start': function () {
            this.getAvailability();
        },
        'end': function () {
            this.getAvailability();
        },
        'filters.invoiceStatusOpen': function () {
            this.getAvailability();
        },
        'filters.invoiceStatusPartial': function () {
            this.getAvailability();
        },
    },
    created() {
        this.loadClubRoles();
        this.loadSepaExports();
        this.getAvailability();
    },
    methods: {
        loadSepaExports() {
            this.loading = true;
            SepaDirectDebitExportRepository.findByClub(this.clubId)
                .then(response => {
                    this.sepaExports = this.groupExportResponse(response);
                    if (this.reloadExports) {
                        this.reloadExports = false;
                        this.getAvailability();
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        groupExportResponse(sepaExports) {
            let exports = [];
            sepaExports.forEach(sepaExport => {
                const exportAlreadyPushed = exports.filter(item => item.createdAt === sepaExport.createdAt).length > 0;
                if (exportAlreadyPushed) {
                    return;
                }
                const fileCount = sepaExports.filter(item => item.createdAt === sepaExport.createdAt).length;
                if (fileCount > 1) {
                    sepaExport.isZipFile = true;
                    sepaExport.fileCount = fileCount;
                    let numberOfInvoices = 0;
                    for (let i = 0; i < sepaExports.length; i++) {
                        if (sepaExports[i].createdAt === sepaExport.createdAt) {
                            numberOfInvoices += sepaExports[i].numberOfInvoices;
                        }
                    }
                    sepaExport.numberOfInvoices = numberOfInvoices;
                }
                exports.push(sepaExport);
            });
            return exports;
        },
        createSepaExport() {
            this.loadingCreate = true;
            const filters = this.invoiceStatus.map(invoiceStatus => {
                return {status: invoiceStatus};
            });
            const date = parse(this.filters.valuationDate, 'dd.MM.yyyy', new Date());
            const params = {
                filters: filters,
                valuationDate: {day: date.getDate(), month: date.getMonth()+1, year: date.getFullYear()},
                splitFiles: this.filters.splitFiles,
            };
            if (this.filters.splitFiles) {
                params.maxTransactionsPerFile = this.filters.maxTransactionsPerFile;
            }
            const parsedStart = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(parsedStart, 'yyyy-MM-dd');
            const parsedEnd = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(parsedEnd, 'yyyy-MM-dd');
            SepaDirectDebitExportRepository.createByClub(this.clubId, formattedStart, formattedEnd, params)
                .then(() => {
                    this.error = new Error();
                    this.loadSepaExports();
                    this.getAvailability();
                })
                .catch(error => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => this.loadingCreate = false);
        },
        formatDateTime(date) {
            if (date === null) {
                return '';
            }
            const parsedDate = parse(date, 'yyyy-MM-dd HH:mm:ss', new Date());

            return format(parsedDate, 'dd.MM.yyyy HH:mm');
        },
        formatDate(date) {
            if (date === null) {
                return '';
            }
            const parsedDate = parse(date, 'yyyy-MM-dd', new Date());

            return format(parsedDate, 'dd.MM.yyyy');

        },
        download(sepaExport) {
            if (sepaExport.isZipFile) {
                SepaDirectDebitExportRepository.downloadZip(this.clubId, sepaExport.createdAt)
                    .then(() => {})
                    .catch(() => this.$notify.error('error.loadingData'));
            } else {
                SepaDirectDebitExportRepository.download(sepaExport.id)
                    .then(() => {})
                    .catch(() => this.$notify.error('error.loadingData'));
            }
        },
        withinLastWeek(sepaExport) {
            const date = new Date();
            const dateMinus7Days = subDays(date, 7);
            return sepaExport.createdAt > format(dateMinus7Days, 'yyyy-MM-dd');
        },
        deleteExport(sepaExport) {

            showConfirm(this.$t('confirmInformal'), this.$t('Willst du diesen SEPA-Lastschrift-Export wirklich löschen?'), () => {
                if (sepaExport.isZipFile) {
                    SepaDirectDebitExportRepository.deleteZip(this.clubId, sepaExport.createdAt)
                        .then(() => {
                            this.loadSepaExports();
                            this.getAvailability();
                        })
                        .catch(() => this.$notify.error('Daten konnten nicht gelöscht werden'));
                } else {
                    SepaDirectDebitExportRepository.delete(sepaExport.id)
                        .then(() => {
                            this.loadSepaExports();
                            this.getAvailability();
                        })
                        .catch(() => this.$notify.error('Daten konnten nicht gelöscht werden'));
                }
            }, this.$t('Löschen'));
        },
        getAvailability() {
            if (!this.filters.invoiceStatusOpen && !this.filters.invoiceStatusPartial) {
                this.availability.isAvailable = false;
                this.availability.messages = [];
                return;
            }

            const parsedStart = parse(this.start, 'dd.MM.yyyy', new Date());
            const formattedStart = format(parsedStart, 'yyyy-MM-dd');
            const parsedEnd = parse(this.end, 'dd.MM.yyyy', new Date());
            const formattedEnd = format(parsedEnd, 'yyyy-MM-dd');

            if (!formattedStart || !formattedEnd || parsedStart > parsedEnd) {
                this.availability.isAvailable = false;
                this.availability.messages = [this.$t('Bitte gib einen gültigen Zeitraum an.')];
                return;
            }

            this.loadingAvailability = true;

            const query = '?dateRange=' + formattedStart + ':' + formattedEnd + '&filters=' + this.invoiceStatus.join(',');
            SepaDirectDebitExportRepository.getAvailabilityByClubAndQuery(this.clubId, query)
                .then(response => {
                    this.availability = response;
                    if (this.availability.hasRunningExport) {
                        this.reloadExports = true;
                        setTimeout(() => this.getAvailability(), 5000)
                    } else if (this.reloadExports) {
                        this.loadSepaExports();
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loadingAvailability = false);
        },
        loadClubRoles() {
            ClubRoleRepository.findByClubAndCurrentUser(this.clubId)
                .then(response => response.map(clubRole => this.clubRoles.push(clubRole.name)));
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
